import React, { useState, useEffect, useMemo } from "react";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Swal from "sweetalert2";
import {
  fetchGHDashboardStats,
  fetchGHServicesList,
  deleteGHRecord,
  GH_STATUSES,
} from "../../services/guestHouseServicesApi";
import { fetchGuestHouses } from "../../services/guestHouseApi";
import CreateGHServiceModal from "./components/CreateGHServiceModal";
import GHDetailsModal from "./components/GHDetailsModal";
import AddServicesModal from "./components/AddServicesModal";
import AddPaymentModal from "./components/AddPaymentModal";
import EditStayInfoModal from "./components/EditStayInfoModal";
import "./GuestHouseServices.css";

export default function GuestHouseServices({ embedded = false }) {
  // Stats
  const [stats, setStats] = useState({
    totalRecords: 0,
    activeRecords: 0,
    completedRecords: 0,
    totalBilling: 0,
    totalPaid: 0,
    totalDue: 0,
    totalServicesCount: 0,
  });

  // Records list & filters
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guestHousesCatalog, setGuestHousesCatalog] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedGuestHouse, setSelectedGuestHouse] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const rowsPerPage = 10;

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addServicesOpen, setAddServicesOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [editStayOpen, setEditStayOpen] = useState(false);

  useEffect(() => {
    loadStats();
    loadGuestHouses();
  }, []);

  const loadGuestHouses = async () => {
    try {
      const res = await fetchGuestHouses();
      const list = Array.isArray(res) ? res : res?.data || res?.details || [];
      setGuestHousesCatalog(list);
    } catch (e) {
      console.warn("Could not load guest houses for filter:", e);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [currentPage, selectedStatus, selectedGuestHouse, dateFrom, dateTo, searchTerm]);

  const loadStats = async () => {
    try {
      const data = await fetchGHDashboardStats();
      if (data) setStats(data);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (selectedStatus !== "ALL") params.status = selectedStatus;
      if (selectedGuestHouse !== "ALL") params.guestHouseName = selectedGuestHouse;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const res = await fetchGHServicesList(params);
      const list = res?.data || [];
      setRecords(list);
      setTotalPages(res?.totalPages || 1);
      setTotalCount(res?.totalCount || list.length);
    } catch (err) {
      console.error("Error loading GH service records:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadRecords();
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedStatus("ALL");
    setSelectedGuestHouse("ALL");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  const handleDelete = async (rec) => {
    const result = await Swal.fire({
      title: "Delete Service Record?",
      text: `Are you sure you want to delete ${rec.guestHouseServiceId} for ${rec.patient_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      try {
        await deleteGHRecord(rec._id || rec.guestHouseServiceId);
        Swal.fire("Deleted", "Record removed successfully.", "success");
        loadRecords();
        loadStats();
      } catch (err) {
        Swal.fire("Error", typeof err === "string" ? err : "Failed to delete", "error");
      }
    }
  };

  // Extract unique guest houses for filter dropdown dynamically
  const uniqueGuestHouses = useMemo(() => {
    const fromRecords = records.map((r) => r.guestHouseName).filter(Boolean);
    const fromCatalog = guestHousesCatalog.map((gh) => gh.name || gh.title).filter(Boolean);
    return Array.from(new Set([...fromCatalog, ...fromRecords])).sort();
  }, [records, guestHousesCatalog]);

  const content = (
    <div className="gh-services-page">
      {/* Top Banner (if not embedded) */}
      {!embedded && (
        <div className="gh-services-header d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h4 className="page-title mb-1 fw-bold text-dark" style={{ fontSize: "20px" }}>
              <i className="fa fa-building text-primary me-2" />
              Guest House Services & Bookings
            </h4>
            <p className="text-muted small mb-0">
              Manage patient accommodation, requested room amenities, nursing care, meals, and payment receipts.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
              onClick={() => {
                loadStats();
                loadRecords();
              }}
              title="Refresh data"
            >
              <i className="fa fa-refresh" /> Refresh
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center gap-2 shadow-sm fw-bold px-3"
              style={{ backgroundColor: "#0284c7", borderColor: "#0284c7" }}
              onClick={() => setCreateModalOpen(true)}
            >
              <i className="fa fa-plus" /> New Booking / Service
            </button>
          </div>
        </div>
      )}

      {/* Embedded Action Bar (if embedded in GuestHouses page) */}
      {embedded && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "16px" }}>
              Patient Guest House Bookings & Service Requests
            </h5>
            <small className="text-muted">
              Track active stays, room services, and payments across all guest houses
            </small>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm d-flex align-items-center gap-2 shadow-sm fw-bold px-3"
            style={{ backgroundColor: "#0284c7", borderColor: "#0284c7" }}
            onClick={() => setCreateModalOpen(true)}
          >
            <i className="fa fa-plus" /> New Booking / Service
          </button>
        </div>
      )}

      {/* KPI Stats Bar (7 Cards) */}
      <div className="row g-3 mb-3">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-blue">
              <i className="fa fa-book" />
            </div>
            <div>
              <div className="gh-stat-value">{stats.totalRecords || 0}</div>
              <div className="gh-stat-label">Total Bookings</div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-6">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-emerald">
              <i className="fa fa-bed" />
            </div>
            <div>
              <div className="gh-stat-value">{stats.activeRecords || 0}</div>
              <div className="gh-stat-label">Active Stays</div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-6">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-indigo">
              <i className="fa fa-check-circle" />
            </div>
            <div>
              <div className="gh-stat-value">{stats.completedRecords || 0}</div>
              <div className="gh-stat-label">Completed Stays</div>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-sm-6">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-purple">
              <i className="fa fa-concierge-bell" />
            </div>
            <div>
              <div className="gh-stat-value">{stats.totalServicesCount || 0}</div>
              <div className="gh-stat-label">Services Delivered</div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-4 col-sm-6">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-blue">
              <i className="fa fa-money" />
            </div>
            <div>
              <div className="gh-stat-value">₹{(Number(stats.totalBilling) || 0).toLocaleString()}</div>
              <div className="gh-stat-label">Total Billed Amount</div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-4 col-sm-6">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-emerald">
              <i className="fa fa-check" />
            </div>
            <div>
              <div className="gh-stat-value text-success">
                ₹{(Number(stats.totalPaid) || 0).toLocaleString()}
              </div>
              <div className="gh-stat-label">Total Received</div>
            </div>
          </div>
        </div>

        <div className="col-lg-4 col-md-4 col-sm-12">
          <div className="gh-stat-card-custom">
            <div className="gh-stat-icon-wrapper gh-stat-icon-rose">
              <i className="fa fa-exclamation-circle" />
            </div>
            <div>
              <div className="gh-stat-value text-danger">
                ₹{(Number(stats.totalDue) || 0).toLocaleString()}
              </div>
              <div className="gh-stat-label">Outstanding Balance</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="gh-filter-bar">
        <form onSubmit={handleSearchSubmit} className="row g-2 align-items-center">
          <div className="col-lg-3 col-md-6">
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white">
                <i className="fa fa-search text-muted" />
              </span>
              <input
                type="text"
                placeholder="Search patient, ID, property, service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
          </div>

          <div className="col-lg-2 col-md-3 col-6">
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="form-select form-select-sm"
            >
              <option value="ALL">All Statuses</option>
              {GH_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-2 col-md-3 col-6">
            <select
              value={selectedGuestHouse}
              onChange={(e) => {
                setSelectedGuestHouse(e.target.value);
                setCurrentPage(1);
              }}
              className="form-select form-select-sm"
            >
              <option value="ALL">All Guest Houses</option>
              {uniqueGuestHouses.map((gh) => (
                <option key={gh} value={gh}>
                  {gh}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-2 col-md-3 col-6">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="form-control form-control-sm"
              title="Filter From Date"
            />
          </div>

          <div className="col-lg-2 col-md-3 col-6">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="form-control form-control-sm"
              title="Filter To Date"
            />
          </div>

          <div className="col-lg-1 col-md-12 text-end">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100"
              onClick={handleResetFilters}
              title="Reset All Filters"
            >
              <i className="fa fa-refresh" />
            </button>
          </div>
        </form>
      </div>

      {/* Main Data Table */}
      <div className="gh-table-card">
        <div className="gh-table-header">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
              Service Records
            </h6>
            <span className="badge bg-light text-primary border">{totalCount} Bookings</span>
          </div>

          <div className="d-flex align-items-center gap-2 small text-muted">
            <span>
              Showing Page {currentPage} of {totalPages}
            </span>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table gh-table mb-0">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Patient Details</th>
                <th>Property & Room</th>
                <th>Stay Period</th>
                <th>Services Summary</th>
                <th>Total Billed</th>
                <th>Paid</th>
                <th>Due</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <i className="fa fa-spinner fa-spin fa-2x text-primary mb-2" />
                    <p className="text-muted small mb-0">Loading guest house records...</p>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <div className="text-muted mb-2" style={{ fontSize: "32px" }}>
                      <i className="fa fa-bed" />
                    </div>
                    <h6 className="fw-bold text-dark">No Guest House Service Records Found</h6>
                    <p className="small text-muted mb-3">
                      {searchTerm || selectedStatus !== "ALL"
                        ? "No bookings matched your filter criteria."
                        : "No patient guest house bookings have been recorded yet."}
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm px-3"
                      onClick={() => setCreateModalOpen(true)}
                    >
                      <i className="fa fa-plus me-1" /> Create First Booking
                    </button>
                  </td>
                </tr>
              ) : (
                records.map((rec) => {
                  const bill = Number(rec.final_amount || rec.totalAmount || 0);
                  const paid = Number(rec.paidAmount || 0);
                  const due = Number(rec.dueAmount || Math.max(0, bill - paid));

                  const fromStr =
                    rec.stayDateRangeFrom ||
                    (rec.stayDateRange?.from
                      ? new Date(rec.stayDateRange.from).toLocaleDateString("en-GB")
                      : "-");
                  const toStr =
                    rec.stayDateRangeTo ||
                    (rec.stayDateRange?.to
                      ? new Date(rec.stayDateRange.to).toLocaleDateString("en-GB")
                      : "-");

                  return (
                    <tr key={rec._id || rec.guestHouseServiceId}>
                      {/* ID */}
                      <td>
                        <span
                          className="fw-bold text-primary"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedRecordId(rec._id || rec.guestHouseServiceId);
                            setDetailsModalOpen(true);
                          }}
                        >
                          {rec.guestHouseServiceId || "GHS-Record"}
                        </span>
                      </td>

                      {/* Patient */}
                      <td>
                        <strong className="text-dark d-block">{rec.patient_name || "Patient"}</strong>
                        <small className="text-muted">
                          ID: {rec.patientId}
                          {rec.treatment_id && ` • ${rec.treatment_id}`}
                        </small>
                      </td>

                      {/* Property */}
                      <td>
                        <span className="fw-semibold text-dark d-block">
                          {rec.guestHouseName || "Guest House"}
                        </span>
                        <span className="badge bg-light text-muted border">
                          Room {rec.roomNumber || "N/A"}
                        </span>
                      </td>

                      {/* Stay Period */}
                      <td className="text-nowrap small text-muted">
                        <div>
                          <i className="fa fa-calendar me-1 text-primary" />
                          {fromStr}
                        </div>
                        <div className="text-muted">to {toStr}</div>
                      </td>

                      {/* Services Summary */}
                      <td>
                        {Array.isArray(rec.services) && rec.services.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1" style={{ maxWidth: "220px" }}>
                            {rec.services.map((s, idx) => (
                              <span
                                key={idx}
                                className="badge bg-light text-dark border"
                                style={{ fontSize: "11px" }}
                              >
                                {s.serviceName}
                                {s.quantity > 1 && ` (${s.quantity})`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>

                      {/* Financials */}
                      <td className="fw-bold text-dark">₹{bill.toLocaleString()}</td>
                      <td className="fw-semibold text-success">₹{paid.toLocaleString()}</td>
                      <td>
                        <span className={`badge ${due > 0 ? "bg-danger" : "bg-light text-muted border"}`}>
                          ₹{due.toLocaleString()}
                        </span>
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className={`badge ${
                            rec.status === "Completed"
                              ? "bg-success"
                              : rec.status === "Cancelled"
                              ? "bg-danger"
                              : "bg-primary"
                          }`}
                          style={{ fontSize: "11.5px" }}
                        >
                          {rec.status || "Active"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="text-end">
                        <div className="d-inline-flex gap-1">
                          <button
                            type="button"
                            className="action-btn-circle"
                            onClick={() => {
                              setSelectedRecordId(rec._id || rec.guestHouseServiceId);
                              setDetailsModalOpen(true);
                            }}
                            title="View Record Details"
                          >
                            <i className="fa fa-eye" />
                          </button>

                          <button
                            type="button"
                            className="action-btn-circle"
                            onClick={() => {
                              setSelectedRecord(rec);
                              setAddServicesOpen(true);
                            }}
                            title="Add More Services"
                          >
                            <i className="fa fa-plus" />
                          </button>

                          <button
                            type="button"
                            className="action-btn-circle text-success"
                            onClick={() => {
                              setSelectedRecord(rec);
                              setAddPaymentOpen(true);
                            }}
                            title="Record Payment"
                          >
                            <i className="fa fa-credit-card" />
                          </button>

                          <button
                            type="button"
                            className="action-btn-circle"
                            onClick={() => {
                              setSelectedRecord(rec);
                              setEditStayOpen(true);
                            }}
                            title="Edit Stay Info"
                          >
                            <i className="fa fa-pencil" />
                          </button>

                          <button
                            type="button"
                            className="action-btn-circle text-danger"
                            onClick={() => handleDelete(rec)}
                            title="Delete Record"
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="small text-muted">
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} records
            </span>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, p) => setCurrentPage(p)}
                color="primary"
                size="small"
              />
            </Stack>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateGHServiceModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          loadRecords();
          loadStats();
        }}
      />

      <GHDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedRecordId(null);
        }}
        recordId={selectedRecordId}
        onRecordUpdated={() => {
          loadRecords();
          loadStats();
        }}
      />

      <AddServicesModal
        open={addServicesOpen}
        onClose={() => {
          setAddServicesOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        onSuccess={() => {
          loadRecords();
          loadStats();
        }}
      />

      <AddPaymentModal
        open={addPaymentOpen}
        onClose={() => {
          setAddPaymentOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        onSuccess={() => {
          loadRecords();
          loadStats();
        }}
      />

      <EditStayInfoModal
        open={editStayOpen}
        onClose={() => {
          setEditStayOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        onSuccess={() => {
          loadRecords();
          loadStats();
        }}
      />
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="page-wrapper">
      <div className="content">{content}</div>
    </div>
  );
}
